/**
 * Animation utilities and variants for Framer Motion
 * Provides consistent animation patterns across the app
 */

import { Variants } from 'framer-motion';

// Easing functions for smooth animations
export const EASING = {
  smooth: [0.43, 0.13, 0.23, 0.96],
  bounce: [0.68, -0.55, 0.265, 1.55],
  fast: [0.25, 0.46, 0.45, 0.94],
  slow: [0.65, 0, 0.35, 1],
};

// Duration constants
export const DURATION = {
  fast: 0.2,
  normal: 0.3,
  slow: 0.5,
  slower: 0.7,
};

// Fade in from bottom (for cards and posts)
export const fadeInUp: Variants = {
  hidden: {
    opacity: 0,
    y: 40,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: DURATION.slow,
      ease: EASING.smooth,
    },
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: {
      duration: DURATION.fast,
    },
  },
};

// Fade in from top (for headers and notifications)
export const fadeInDown: Variants = {
  hidden: {
    opacity: 0,
    y: -40,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: DURATION.slow,
      ease: EASING.smooth,
    },
  },
};

// Scale and fade (for modals and dropdowns)
export const scaleIn: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.9,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: DURATION.normal,
      ease: EASING.smooth,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: {
      duration: DURATION.fast,
    },
  },
};

// Slide in from right (for side panels)
export const slideInRight: Variants = {
  hidden: {
    x: '100%',
    opacity: 0,
  },
  visible: {
    x: 0,
    opacity: 1,
    transition: {
      duration: DURATION.slow,
      ease: EASING.smooth,
    },
  },
  exit: {
    x: '100%',
    opacity: 0,
    transition: {
      duration: DURATION.normal,
    },
  },
};

// Slide in from left (for side panels)
export const slideInLeft: Variants = {
  hidden: {
    x: '-100%',
    opacity: 0,
  },
  visible: {
    x: 0,
    opacity: 1,
    transition: {
      duration: DURATION.slow,
      ease: EASING.smooth,
    },
  },
  exit: {
    x: '-100%',
    opacity: 0,
    transition: {
      duration: DURATION.normal,
    },
  },
};

// Stagger children animation
export const staggerContainer: Variants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

// Button hover and tap animations
export const buttonHover = {
  scale: 1.05,
  transition: {
    duration: DURATION.fast,
    ease: EASING.smooth,
  },
};

export const buttonTap = {
  scale: 0.95,
};

// Icon animations
export const iconSpin = {
  rotate: 360,
  transition: {
    duration: 1,
    ease: 'linear',
    repeat: Infinity,
  },
};

export const iconBounce = {
  y: [0, -10, 0],
  transition: {
    duration: 0.6,
    ease: EASING.bounce,
  },
};

// Card hover effect
export const cardHover = {
  y: -4,
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
  transition: {
    duration: DURATION.normal,
    ease: EASING.smooth,
  },
};

// Notification/Toast animations
export const toastSlideIn: Variants = {
  hidden: {
    x: '100%',
    opacity: 0,
  },
  visible: {
    x: 0,
    opacity: 1,
    transition: {
      duration: DURATION.normal,
      ease: EASING.smooth,
    },
  },
  exit: {
    x: '100%',
    opacity: 0,
    transition: {
      duration: DURATION.fast,
    },
  },
};

// Loading spinner
export const spinnerRotate = {
  rotate: 360,
  transition: {
    duration: 1,
    ease: 'linear',
    repeat: Infinity,
  },
};

// Backdrop animation
export const backdropFade: Variants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      duration: DURATION.normal,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: DURATION.fast,
    },
  },
};

// Pulse animation (for attention)
export const pulse = {
  scale: [1, 1.05, 1],
  transition: {
    duration: 2,
    repeat: Infinity,
    ease: EASING.smooth,
  },
};

// Shake animation (for errors)
export const shake = {
  x: [0, -10, 10, -10, 10, 0],
  transition: {
    duration: 0.5,
  },
};

// Number counter animation helper
export const numberCounter = (from: number, to: number, duration: number = 1) => ({
  from,
  to,
  duration,
  ease: EASING.smooth,
});

// Page transition variants
export const pageTransition: Variants = {
  hidden: {
    opacity: 0,
    x: -20,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: DURATION.slow,
      ease: EASING.smooth,
    },
  },
  exit: {
    opacity: 0,
    x: 20,
    transition: {
      duration: DURATION.normal,
    },
  },
};

// Expand/collapse animation
export const expandCollapse: Variants = {
  collapsed: {
    height: 0,
    opacity: 0,
    transition: {
      duration: DURATION.normal,
      ease: EASING.smooth,
    },
  },
  expanded: {
    height: 'auto',
    opacity: 1,
    transition: {
      duration: DURATION.normal,
      ease: EASING.smooth,
    },
  },
};
